import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  CheckCircle,
  ClockCounterClockwise,
  Gauge,
  Info,
  ListChecks,
  Pause,
  Play,
  Prohibit,
  XCircle,
  WarningCircle,
} from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { cn } from '@/lib/utils';
import {
  isActiveSendJobStatus,
  useCancelSendJob,
  useGetSendJobById,
  useGetSendJobProgress,
  useGetSendJobs,
  usePauseSendJob,
  useResumeSendJob,
} from '../hooks/use-send-jobs';
import type { SendJob, SendJobProgress } from '../types/send-jobs.types';

const terminalStatuses = new Set(['completed', 'failed', 'cancelled', 'sent']);

const Panel = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <Card className={cn('rounded-lg border-neutral-200 bg-white shadow-sm', className)}>
    <CardContent className="p-5 md:p-6">{children}</CardContent>
  </Card>
);

const formatCount = (value: number) => new Intl.NumberFormat().format(value);

const formatDateTime = (value: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
};

const formatDuration = (startValue: string | null, endValue: string | null) => {
  if (!startValue) return 'Not started';
  const start = new Date(startValue);
  const end = endValue ? new Date(endValue) : new Date();
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'Unknown';

  const totalMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
};

const titleCase = (value: string) =>
  value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

const getStatusTone = (status: string) => {
  switch (status) {
    case 'completed':
    case 'sent':
      return 'bg-emerald-50 text-emerald-800';
    case 'failed':
    case 'cancelled':
      return 'bg-destructive/10 text-destructive';
    case 'paused':
      return 'bg-amber-50 text-amber-800';
    default:
      return 'bg-primary/10 text-primary';
  }
};

const getStatusMessage = (status: string, processedCount: number, recipientCount: number) => {
  const processed = formatCount(processedCount);
  const total = formatCount(recipientCount);

  switch (status) {
    case 'completed':
    case 'sent':
      return `Completed. All ${processed} processed recipients were handled.`;
    case 'failed':
      return `Failed. ${processed} of ${total} recipients were processed before the send stopped.`;
    case 'cancelled':
      return `Cancelled. ${processed} of ${total} recipients were processed before cancellation.`;
    case 'paused':
      return `Paused. ${processed} of ${total} recipients have been processed so far.`;
    case 'sending':
      return `Sending. ${processed} of ${total} recipients have been processed.`;
    default:
      return `Queued. ${total} recipients are waiting to be processed.`;
  }
};

const mergeProgress = (job: SendJob | undefined, progress: SendJobProgress | undefined) => ({
  status: progress?.status ?? job?.status ?? 'queued',
  recipientCount: progress?.recipientCount ?? job?.recipientCount ?? 0,
  processedCount: progress?.processedCount ?? job?.processedCount ?? 0,
  sentCount: progress?.sentCount ?? job?.sentCount ?? 0,
  failedCount: progress?.failedCount ?? job?.failedCount ?? 0,
  skippedCount: progress?.skippedCount ?? job?.skippedCount ?? 0,
  skipReasons: progress?.skipReasons?.length ? progress.skipReasons : job?.skipReasons ?? [],
  startedAt: progress?.startedAt ?? job?.startedAt ?? null,
  completedAt: job?.completedAt ?? null,
  estimatedCompletionAt: progress?.estimatedCompletionAt ?? null,
  lastError: progress?.lastError ?? job?.lastError ?? null,
  throughputPerMinute: progress?.throughputPerMinute ?? 0,
  queuedForSeconds: progress?.queuedForSeconds ?? 0,
  dispatcherOnline: progress?.dispatcherOnline ?? null,
});

const LoadingState = () => (
  <div className="flex flex-col gap-5">
    <Skeleton className="h-11 w-full rounded-lg" />
    <Skeleton className="h-[330px] w-full rounded-lg" />
    <Skeleton className="h-[210px] w-full rounded-lg" />
  </div>
);

type PreSendCheckRow = {
  label: string;
  pass: boolean;
  detail?: string;
  advisory?: boolean;
};

export const SendsPage = () => {
  const { toast } = useToast();
  const [selectedJobId, setSelectedJobId] = useState('');
  const [preSendChecksOpen, setPreSendChecksOpen] = useState(false);
  const jobsQuery = useGetSendJobs({ limit: 10, offset: 0, sort: 'createdDate', order: 'desc' });
  const jobs = useMemo(() => jobsQuery.data?.items ?? [], [jobsQuery.data?.items]);

  useEffect(() => {
    if (!selectedJobId && jobs[0]?.id) {
      setSelectedJobId(jobs[0].id);
    }
  }, [jobs, selectedJobId]);

  const selectedSummary = jobs.find((job) => job.id === selectedJobId);
  const jobQuery = useGetSendJobById(selectedJobId, { enabled: Boolean(selectedJobId) });
  const statusForPolling = jobQuery.data?.status ?? selectedSummary?.status;
  const progressQuery = useGetSendJobProgress(selectedJobId, {
    enabled: Boolean(selectedJobId),
    status: statusForPolling,
  });

  const pauseJob = usePauseSendJob();
  const resumeJob = useResumeSendJob();
  const cancelJob = useCancelSendJob();

  const job = useMemo(
    () =>
      jobQuery.data
        ? {
            ...jobQuery.data,
            campaignName: selectedSummary?.campaignName ?? jobQuery.data.campaignName,
          }
        : selectedSummary,
    [jobQuery.data, selectedSummary]
  );
  const progress = useMemo(
    () => mergeProgress(job, progressQuery.data),
    [job, progressQuery.data]
  );
  const status = progress.status.toLowerCase();
  const progressPercent =
    progress.recipientCount > 0
      ? Math.min(100, Math.round((progress.processedCount / progress.recipientCount) * 100))
      : 0;
  const skippedRemainder = Math.max(
    0,
    progress.skippedCount -
      progress.skipReasons.reduce((sum, item) => sum + item.count, 0)
  );
  const skipReasons =
    skippedRemainder > 0
      ? [...progress.skipReasons, { reason: 'Other skipped recipients', count: skippedRemainder }]
      : progress.skipReasons;
  const isTerminal = terminalStatuses.has(status);
  const isBusy = pauseJob.isPending || resumeJob.isPending || cancelJob.isPending;
  const canPause = status === 'sending' || status === 'queued';
  const canResume = status === 'paused';
  const canCancel = isActiveSendJobStatus(status);
  const startedAt = formatDateTime(progress.startedAt);
  const completedAt = formatDateTime(progress.completedAt);
  const estimatedCompletionAt = formatDateTime(progress.estimatedCompletionAt);
  const duration = formatDuration(progress.startedAt, progress.completedAt);

  const kpis = [
    { label: 'Sent', value: formatCount(progress.sentCount) },
    { label: 'Failed', value: formatCount(progress.failedCount) },
    { label: 'Skipped', value: formatCount(progress.skippedCount) },
    { label: 'Duration', value: duration },
  ];

  const preSendChecks: PreSendCheckRow[] = [
    { label: 'Content saved', pass: true },
    { label: 'Subject line set', pass: true },
    { label: 'Sender identity set', pass: true },
    {
      label: 'Audience selected',
      pass: progress.recipientCount > 0,
      detail: 'Pick at least one list or segment.',
    },
    {
      label: 'Unsubscribe link present',
      pass: true,
      detail: 'Every marketing send must carry an unsubscribe link.',
    },
    {
      label: 'At least one sendable recipient',
      pass: progress.recipientCount > 0,
      detail: 'Every contact in this audience is unsubscribed, pending or suppressed.',
    },
    {
      label: 'Plain-text version present',
      pass: false,
      detail: 'Recommended for deliverability.',
      advisory: true,
    },
  ];
  const checkedCount = preSendChecks.filter((item) => item.pass || item.advisory).length;

  const runAction = async (
    action: 'pause' | 'resume' | 'cancel',
    mutation: typeof pauseJob | typeof resumeJob | typeof cancelJob
  ) => {
    if (!selectedJobId) return;

    await mutation.mutateAsync(selectedJobId);
    toast({
      title: `Send ${action} requested`,
      description: 'The send job status will refresh automatically.',
    });
  };

  return (
    <div className="mx-auto flex h-full w-full max-w-[880px] flex-col gap-5 pb-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">Send progress</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Watch a send run, and control it while it is in flight.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            className="bg-white"
            disabled={!job}
            onClick={() => setPreSendChecksOpen(true)}
          >
            <ListChecks size={16} />
            Pre-send checks
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              void jobsQuery.refetch();
              document
                .getElementById('send-history-section')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}
          >
            <ClockCounterClockwise size={16} />
            Send history
          </Button>
        </div>
      </header>

      {jobsQuery.isLoading ? <LoadingState /> : null}

      {jobsQuery.isError ? (
        <Panel>
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="grid size-11 place-items-center rounded-full bg-destructive/10 text-destructive">
              <WarningCircle size={22} />
            </span>
            <p className="font-semibold text-foreground">Could not load send jobs</p>
            <p className="max-w-[360px] text-sm text-muted-foreground">
              The SendJobs API did not return a usable response.
            </p>
            <Button type="button" onClick={() => jobsQuery.refetch()}>
              Retry
            </Button>
          </div>
        </Panel>
      ) : null}

      {!jobsQuery.isLoading && !jobsQuery.isError && jobs.length === 0 ? (
        <Panel>
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <span className="grid size-11 place-items-center rounded-full bg-primary/10 text-primary">
              <ListChecks size={22} />
            </span>
            <p className="font-semibold text-foreground">No send jobs yet</p>
            <p className="max-w-[420px] text-sm text-muted-foreground">
              Once a campaign starts sending, this page will show its progress and controls.
            </p>
          </div>
        </Panel>
      ) : null}

      {job ? (
        <>
          <div
            className={cn(
              'flex items-center gap-2 rounded-lg px-3.5 py-3 text-sm',
              getStatusTone(status)
            )}
          >
            {status === 'failed' || status === 'cancelled' ? (
              <WarningCircle size={16} className="shrink-0" />
            ) : (
              <CheckCircle size={16} className="shrink-0" />
            )}
            <p>{getStatusMessage(status, progress.processedCount, progress.recipientCount)}</p>
          </div>

          <Panel>
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl font-semibold tracking-tight text-foreground">
                    {job.campaignName}
                  </h2>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
                      getStatusTone(status)
                    )}
                  >
                    <span className="size-1.5 rounded-full bg-current" />
                    {titleCase(status)}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Trigger: {titleCase(job.mode)}
                  {startedAt ? ` · Started ${startedAt}` : ''}
                  {completedAt ? ` · Finished ${completedAt}` : ''}
                  {estimatedCompletionAt ? ` · ETA ${estimatedCompletionAt}` : ''}
                  {job.id ? ` · Job ${job.id}` : ''}
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-md bg-neutral-50 px-2.5 py-1.5 text-xs text-neutral-500">
                <Gauge size={13} />
                {job.throttlePerHour ? `${formatCount(job.throttlePerHour)} / hour` : 'No limit'}
              </span>
            </div>

            <div className="mt-5 flex flex-col gap-2">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-semibold leading-none text-foreground">
                    {formatCount(progress.processedCount)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    / {formatCount(progress.recipientCount)} recipients
                  </span>
                </div>
                <span className="text-base font-semibold text-primary">{progressPercent}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-neutral-100">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {kpis.map((item) => (
                <div key={item.label} className="rounded-lg border border-neutral-200 bg-white p-4">
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="mt-1 text-2xl font-semibold leading-none text-foreground">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>

            {progress.lastError ? (
              <div className="mt-4 rounded-lg bg-destructive/10 px-3.5 py-3 text-sm text-destructive">
                {progress.lastError}
              </div>
            ) : null}
          </Panel>

          <Panel>
            <div>
              <h2 className="text-base font-semibold text-foreground">
                Where the rest of the audience went
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Every recipient is accounted for by the SendJobs progress endpoint.
              </p>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              {skipReasons.length > 0 ? (
                skipReasons.map((item) => (
                  <div
                    key={item.reason}
                    className="flex items-center justify-between gap-4 rounded-md bg-neutral-50 px-3.5 py-3 text-sm"
                  >
                    <span className="text-foreground">{titleCase(item.reason)}</span>
                    <span className="font-medium text-foreground">{formatCount(item.count)}</span>
                  </div>
                ))
              ) : (
                <div className="rounded-md bg-neutral-50 px-3.5 py-3 text-sm text-muted-foreground">
                  No skipped recipients reported.
                </div>
              )}
            </div>

            <div className="mt-3 flex items-center gap-1.5 border-t border-neutral-100 pt-3 text-sm text-muted-foreground">
              <CheckCircle size={14} className="shrink-0 text-emerald-600" />
              <span>
                {formatCount(progress.recipientCount)} recipients ={' '}
                {formatCount(progress.sentCount)} sent + {formatCount(progress.failedCount)} failed
                + {formatCount(progress.skippedCount)} skipped
              </span>
            </div>
          </Panel>

          <Panel>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-foreground">Controls</h2>
                <p className="mt-3 max-w-[620px] text-sm leading-6 text-muted-foreground">
                  {isTerminal
                    ? 'This send is terminal, so it cannot be paused, resumed or cancelled.'
                    : 'Use the SendJobs control endpoints to pause, resume or cancel this send.'}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canPause || isBusy}
                  loading={pauseJob.isPending}
                  onClick={() => runAction('pause', pauseJob)}
                >
                  <Pause size={16} />
                  Pause
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canResume || isBusy}
                  loading={resumeJob.isPending}
                  onClick={() => runAction('resume', resumeJob)}
                >
                  <Play size={16} />
                  Resume
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={!canCancel || isBusy}
                  loading={cancelJob.isPending}
                  onClick={() => runAction('cancel', cancelJob)}
                >
                  <Prohibit size={16} />
                  Cancel
                </Button>
              </div>
            </div>

            {jobs.length > 0 ? (
              <div id="send-history-section" className="mt-4 border-t border-neutral-100 pt-4">
                <label
                  htmlFor="send-job-history"
                  className="mb-1.5 block text-xs font-medium text-muted-foreground"
                >
                  Send history
                </label>
                <select
                  id="send-job-history"
                  value={selectedJobId}
                  onChange={(event) => setSelectedJobId(event.target.value)}
                  className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm outline-none focus:ring-1 focus:ring-ring sm:max-w-[420px]"
                >
                  {jobs.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.campaignName} · {titleCase(item.status)}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </Panel>
        </>
      ) : null}

      <p className="text-center text-sm text-muted-foreground">
        Send progress is powered by /v1/send-jobs and /v1/send-jobs/itemId/progress.
      </p>

      <Dialog open={preSendChecksOpen} onOpenChange={setPreSendChecksOpen}>
        <DialogContent
          className="max-w-[480px] gap-0 overflow-hidden rounded-2xl border-0 bg-white p-0 shadow-2xl"
          overlayClassName="bg-black/35"
          hideClose
        >
          <DialogHeader className="border-b border-neutral-200 px-4 py-4">
            <DialogTitle className="text-base font-semibold leading-6 text-foreground">
              Pre-send checks — {job?.campaignName ?? 'Send job'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex gap-4 px-4 py-4">
            <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-full bg-amber-50 text-amber-700">
              <Info size={17} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm leading-5 text-muted-foreground">
                What preSendChecks() verified before this send was allowed to start (
                {checkedCount}/{preSendChecks.length}).
              </p>

              <div className="mt-2 divide-y divide-neutral-100">
                {preSendChecks.map((item) => {
                  const passed = item.pass || item.advisory;
                  return (
                    <div key={item.label} className="flex gap-3 py-3">
                      {passed ? (
                        <CheckCircle
                          size={15}
                          className="mt-0.5 shrink-0 text-emerald-600"
                        />
                      ) : (
                        <XCircle size={15} className="mt-0.5 shrink-0 text-destructive" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {item.label}
                          {item.advisory ? (
                            <span className="font-normal text-muted-foreground"> (advisory)</span>
                          ) : null}
                        </p>
                        {item.detail && (!item.pass || item.advisory) ? (
                          <p className="mt-0.5 text-sm leading-5 text-muted-foreground">
                            {item.detail}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter className="border-t border-neutral-200 px-4 py-3">
            <DialogClose asChild>
              <Button type="button">OK</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
